import React, { useEffect, useState } from 'react';

import { ActionIcon, Tooltip } from '@mantine/core';
import { IconPrinter } from '@tabler/icons-react';
import { ExportToCsv, Options } from 'export-to-csv';
import { useTracking } from 'hooks/mixpanel/useTracking';
import useUncreditedReservations from 'hooks/reports/reservations/uncredited-reservations/useUncreditedReservations';
import { useUncreditedReservationsContext } from 'pages/private/owner/reports/UncreditedReservations/UncreditedReservationsContext';
import { UncreditedReservationsReport } from 'types/generated/models/UncreditedReservationsReport';

interface DownloadUncreditedReservationsCSVProps {
  className?: string;
}

const csvOptions: Options = {
  fieldSeparator: ',',
  quoteStrings: '"',
  decimalSeparator: '.',
  showLabels: true,
  useBom: true,
  useKeysAsHeaders: true,
};

const DownloadUncreditedReservationsCSV = ({
  className,
}: DownloadUncreditedReservationsCSVProps): JSX.Element => {
  const { computedFilters } = useUncreditedReservationsContext();
  const tracking = useTracking();
  const [buttonClicked, setButtonClicked] = useState<boolean>(false);

  const { data, isSuccess, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useUncreditedReservations({
      limit: '100',
      startDate: computedFilters.startDate,
      endDate: computedFilters.endDate,
      eventTypeIds: computedFilters.eventTypeIds,
      search: computedFilters.search,
    });

  useEffect(() => {
    if (buttonClicked && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, buttonClicked]);

  useEffect(() => {
    if (buttonClicked && isSuccess && !hasNextPage) {
      const csvExporter = new ExportToCsv(csvOptions);
      const exportData = data.pages.map(page => page.data.results).flat();

      const flattenedData = exportData.flatMap((report: UncreditedReservationsReport) => {
        if (!report.uncreditedReservations?.length) {
          return [];
        }

        return report.uncreditedReservations.map(event => ({
          'Athlete ID': report.athleteId,
          'Athlete Name': report.athleteFullName || 'N/A',
          'Current Packages': report.activePackages.length > 0 ? report.activePackages.join('; ') : 'N/A',
          'Event Type': event.type,
          'Event Type Name': event.eventTypeName,
          'Event Date': event.startDate,
          'Event Time': event.startTime,
          Coaches: event.coaches.map(coach => coach.fullName).join('; ') || 'N/A',
        }));
      });

      if (flattenedData.length) {
        csvExporter.generateCsv(flattenedData);
        tracking.trackReportGenerated(
          'uncredited-reservations',
          computedFilters.startDate ?? null,
          computedFilters.endDate ?? null,
        );
      }
      setButtonClicked(false);
    }
  }, [isSuccess, hasNextPage, buttonClicked, data?.pages, computedFilters, tracking]);
  return (
    <Tooltip label='Export data as CSV' className={className}>
      <ActionIcon
        onClick={() => setButtonClicked(true)}
        loading={buttonClicked && (isLoading || hasNextPage)}
      >
        <IconPrinter />
      </ActionIcon>
    </Tooltip>
  );
};

export default DownloadUncreditedReservationsCSV;
